import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import {
  Box,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Card,
  Stack
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { FormInputText } from 'components/FormInputText'
import { FormInputDatePicker } from 'components/FormInputDatePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Button } from 'components/Button'
import dayjs from 'dayjs'
import { DataGridSelectAthleteToCopy } from './components/DataGridSelectAthleteToCopy'
import { ColumnsToCopy } from './components/ColumnsToCopy'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import api from 'services/api'
import useResponsive from 'hooks/useResponsive'
import { DataGridHeader } from './components/DataGridHeader'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { setSummaryProfessionalAthlete } from 'redux/reducers/summary-athlete/actions'
import { ProfessionalAthlete } from 'models/interfaces/professional-athlete.interface'

type ProfessionalAthleteRoutineFormProps = {
  id: string
  name: string
  startedAt: string
  dueDateAt?: string
  visibleAfterDueDate?: boolean
}

type Props = {
  isVisible: boolean
  athleteRoutine: ProfessionalAthleteRoutineFormProps
  professionalAthleteId: any
  onClose: () => void
}

export const CopyRoutineToModal = ({ isVisible, athleteRoutine, professionalAthleteId, onClose }: Props) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const dateNow = new Date()
  const dateFuture = new Date()
  const [started, setStarted] = useState<any | undefined>(undefined)
  const [dueDate, setDueDate] = useState<any | undefined>(undefined)
  const [visibleAfterDueDate, setVisibleAfterDueDate] = useState(false)
  const [isCheckDate, setIsCheckDate] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [dueDateAtMessageError, setDueDateAtMessageError] = useState('')
  const [visibleAfterDueDateMessageError, setVisibleAfterDueDateMessageError] = useState('')
  const [professionalAthleteResult, setProfessionalAthleteResult] = useState<ProfessionalAthlete[]>([])
  const [professionalAthleteToCopyResult, setProfessionalAthleteToCopyResult] = useState<ProfessionalAthlete[]>([])
  const [professionalAthletesSelectedToCopy, setProfessionalAthletesSelectedToCopy] = useState<ProfessionalAthlete[]>([])
  const [saveAndGoToAthlete, setSaveAndGoToAthlete] = useState(false)
  const mdUp = useResponsive('up', 'md')

  const athleteRoutineSchema = yup.object({
    id: yup.string().required(() => t('general.validator.empty')).nullable(),
    name: yup.string().trim().required(() => t('general.validator.empty')),
    startedAt: yup.string().required(() => t('general.validator.empty')).nullable(),
    dueDateAt: yup.string().nullable(),
    visibleAfterDueDate: yup.boolean().nullable()
  })
  const { control, handleSubmit, reset, setValue, formState: { errors } }: any = useForm<ProfessionalAthleteRoutineFormProps>({
    resolver: yupResolver(athleteRoutineSchema)
  })

  useEffect(() => {
    if (isVisible) {
      dataFetch().catch(console.error)
      reset()
      setProfessionalAthletesSelectedToCopy([])
      setVisibleAfterDueDateMessageError('')
      setDueDateAtMessageError('')
      setValue('id', athleteRoutine.id)
      setStarted(moment(dateNow, 'DD/MM/YYYY').format('YYYY-MM-DD'))
      setDueDate(moment(new Date(dateFuture.setMonth(dateFuture.getMonth() + 1)), 'DD/MM/YYYY').format('YYYY-MM-DD'))
      setVisibleAfterDueDate(athleteRoutine.visibleAfterDueDate ? athleteRoutine.visibleAfterDueDate : false)
      setIsCheckDate(true)
      setSaveAndGoToAthlete(false)
    }
  }, [isVisible])

  useEffect(() => {
    if (isCheckDate) {
      setVisibleAfterDueDateMessageError('')
      setDueDateAtMessageError('')
      if (visibleAfterDueDate && (!dayjs(dueDate) || dueDate === null || dueDate === undefined)) {
        setVisibleAfterDueDateMessageError(`${t('routine.checkVisibleAfterDueDateAndDueDateEmpty')}`)
      }
      if (dueDate !== undefined && dayjs(dueDate) && dayjs(dueDate) < dayjs(started)) {
        setDueDateAtMessageError(`${t('routine.invalidPeriod')}`)
      }
    }
    setIsCheckDate(false)
  }, [isCheckDate])

  const dataFetch = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('professional-athletes?active=true')
      setProfessionalAthleteToCopyResult(data)
      setProfessionalAthleteResult(data)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: any) => {
    if (!e.target.value) {
      setProfessionalAthleteToCopyResult(professionalAthleteResult)
      return
    }
    setProfessionalAthleteToCopyResult([])
    const dataFound: ProfessionalAthlete[] = []
    const searchText = e.target.value
    const regex = new RegExp(searchText.normalize('NFD').replace(/[\u0300-\u036f]/g, ''), 'i')
    professionalAthleteResult.forEach((obj) => {
      let firstName = ''
      let surName = ''
      Object.entries(obj.athlete).forEach(([key, value]) => {
        const textValue = value
        if (key === 'name') { firstName = textValue }
        if (key === 'surname') { surName = textValue }
        if (!dataFound.includes(obj) &&
          (key === 'email' || key === 'name' || key === 'surname') &&
          regex.exec(textValue.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
          dataFound.push(obj)
        }
      })
      if (!dataFound.includes(obj) &&
        regex.exec((`${firstName} ${surName}`).normalize('NFD').replace(/[\u0300-\u036f]/g, ''))) {
        dataFound.push(obj)
      }
    })
    setProfessionalAthleteToCopyResult(dataFound)
  }

  const handleCopyRoutine = async (professionalAthleteRoutine: ProfessionalAthleteRoutineFormProps): Promise<void> => {
    if (dueDateAtMessageError || visibleAfterDueDateMessageError) {
      return
    }
    if (!professionalAthleteRoutine) {
      dispatch(showMessage({ message: `${t('routine.forCopyNotFound')}`, type: 'error' }))
      return
    }
    if (professionalAthletesSelectedToCopy.length < 1) {
      dispatch(showMessage({ message: `${t('athlete.selectToCopy')}`, type: 'error' }))
      return
    }
    try {
      const professionalAthleteIds = professionalAthletesSelectedToCopy?.map(({ id }) => id)
      await api.post(`/professional-athlete-routines/${professionalAthleteRoutine.id}/copy-professional-athlete`, {
        professionalAthleteIds,
        name: professionalAthleteRoutine.name,
        startedAt: (professionalAthleteRoutine.startedAt ? moment(new Date(professionalAthleteRoutine.startedAt), 'DD/MM/YYYY').format('YYYY-MM-DD') : moment(new Date(), 'DD/MM/YYYY').format('YYYY-MM-DD')),
        dueDateAt: (professionalAthleteRoutine.dueDateAt ? moment(new Date(professionalAthleteRoutine.dueDateAt), 'DD/MM/YYYY').format('YYYY-MM-DD') : ''),
        visibleAfterDueDate: professionalAthleteRoutine.visibleAfterDueDate
      },
      { headers: { 'Content-Type': 'application/json' } })
      dispatch(showMessage({ message: `${t('routine.copySuccess')}` }))
      if (saveAndGoToAthlete && professionalAthleteIds.length === 1) {
        dispatch(setSummaryProfessionalAthlete(professionalAthletesSelectedToCopy[0]))
        navigate(`/athletes/${professionalAthleteIds[0]}/routines`, { state: { reloadRoutines: true } })
      }
      onClose()
    } catch (e) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <Box>
      <Dialog
        fullWidth={true}
        open={isVisible}
        maxWidth="lg"
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
      >
        <DialogTitle>{`${t('routine.title.copyToAthlete')}`}</DialogTitle>
        <DialogContent>
          <Container component="main">
            <Box sx={{ width: '100%', typography: 'body1', mt: 3 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={5} >
                  <Controller
                    name="name"
                    control={control}
                    defaultValue={athleteRoutine?.name}
                    render={({ field: { onChange, value } }) => (
                      <FormInputText
                        name="name"
                        value={value}
                        control={control}
                        placeholder={`${t('routine.field.name.placeholder')}`}
                        label={`${t('routine.field.name.label')}`}
                        onChange={onChange}
                        errorMessage={errors.name?.message}
                      />
                    )} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormInputDatePicker
                    name="startedAt"
                    value={moment(dateNow, 'DD/MM/YYYY').format('YYYY-MM-DD')}
                    required={true}
                    control={control}
                    label={`${t('routine.field.startedAt.label')}`}
                    onAction={(value) => {
                      setStarted(value)
                      setIsCheckDate(true)
                    }}
                    onClear={true}
                    errorMessage={errors.startedAt?.message}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormInputDatePicker
                    name="dueDateAt"
                    value={moment(new Date(dateFuture.setMonth(dateFuture.getMonth() + 1)), 'DD/MM/YYYY').format('YYYY-MM-DD')}
                    control={control}
                    onClear={true}
                    onAction={(value) => {
                      setDueDate(value)
                      setIsCheckDate(true)
                    }}
                    label={`${t('routine.field.dueDateAt.label')}`}
                    errorMessage={dueDateAtMessageError || visibleAfterDueDateMessageError}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="visibleAfterDueDate"
                        control={control}
                        defaultValue={athleteRoutine?.visibleAfterDueDate}
                        render={({ field: { onChange, value } }) => (
                          <Checkbox
                            checked={value ?? false}
                            name="visibleAfterDueDate"
                            onChange={(e) => {
                              onChange(e.target.checked)
                              setVisibleAfterDueDate(e.target.checked)
                              setIsCheckDate(true)
                            }}
                          />
                        )}
                      />
                    }
                    label={`${t('routine.field.visibleAfterDueDate.label')}`}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <Stack spacing={2} padding={1.5}>
                      <DataGridHeader
                        onActionSearch={(value) => handleSearch(value)}
                        onSearchTitle={`${t('search.titleAthlete')}`}
                      />
                      <DataGridSelectAthleteToCopy
                        setProfessionalAthletesSelectedToCopy={setProfessionalAthletesSelectedToCopy}
                        rows={professionalAthleteToCopyResult}
                        columns={mdUp ? ColumnsToCopy : ColumnsToCopy.filter((column) => (column.field !== 'email'))}
                        initialState={{
                          sorting: {
                            sortModel: [{ field: 'name', sort: 'asc' }]
                          }
                        }}
                        hideFooterSelectedRowCount={!mdUp}
                        sx={{
                          '&.MuiDataGrid-root .css-2ovxb4-MuiContainer-root': {
                            paddingLeft: 0
                          }
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </DialogContent>
        <DialogActions>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="flex-end"
            alignItems={{ sm: 'center' }}
            spacing={2}
            width={'100%'}
          >
            {
              professionalAthleteResult && professionalAthletesSelectedToCopy.length === 1 &&
              <LoadingButton loading={loading} onClickCapture={() => setSaveAndGoToAthlete(true)} onClick={handleSubmit(handleCopyRoutine)} variant="contained">
                {t('athlete.btn.saveAndGoToAthlete')}
              </LoadingButton>
            }
            { professionalAthleteResult &&
              <LoadingButton loading={loading} onClickCapture={() => setSaveAndGoToAthlete(false)} onClick={handleSubmit(handleCopyRoutine)} variant="contained">
                {t('general.btn.save')}
              </LoadingButton>
            }
            <Button title={t('general.btn.dismiss')} onClick={onClose} variant="contained" color="secondary" />
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
